import { PeopleAlt, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Divider, IconButton, Stack, Typography, Box } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { ScreenMobile } from "../../components/common/ScreenMobile";
import { CardProfessional } from "./components/CardProfessional";
import { HeaderScheduling } from "./components/HeaderScheduling";
import { SlotsMobile } from "./components/SlotsMobile";

interface IProps {
  open: boolean;
  onClose: VoidFunction;
  location: fhir5.Location;
  practitioners: any;
  slots: fhir5.BundleEntry<fhir5.Slot> | any;
  loading: boolean;
  setRefetchSlots: Dispatch<SetStateAction<boolean>>;
  selectedPractitioner: number;
  setSelectedPractitioner: Dispatch<SetStateAction<number>>;
  scheduleCompleted?: any;
  startDate?: string;
}

const PRACTITIONERS_PER_PAGE = 3;

export function SchedulingScreenMobile({
  open,
  onClose,
  location,
  practitioners,
  slots,
  loading,
  setRefetchSlots,
  selectedPractitioner,
  setSelectedPractitioner,
  scheduleCompleted,
  startDate,
}: IProps) {
  const [page, setPage] = useState(0);

  // Prepara lista ordenada mantendo índice original
  const sortedPractitioners = useMemo(() => {
    if (!practitioners?.length) return [];

    const withIndex = practitioners.map((p: any, index: number) => ({
      ...p,
      originalIndex: index,
    }));

    return withIndex.sort((a: any, b: any) => {
      const nameA = a?.display?.toLowerCase() || "";
      const nameB = b?.display?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });
  }, [practitioners]);

  // Paginação
  const startIndex = page * PRACTITIONERS_PER_PAGE;
  const endIndex = startIndex + PRACTITIONERS_PER_PAGE;
  const visiblePractitioners = sortedPractitioners.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedPractitioners.length / PRACTITIONERS_PER_PAGE);

  // Reseta página quando lista muda
  useEffect(() => {
    setPage(0);
  }, [practitioners]);

  return (
    <ScreenMobile title="Agendamento" open={open} onClose={onClose}>
      <Stack gap={1} mt={1} width="100%" flex={1} sx={{ overflowY: "auto", pb: 4, minHeight: 0 }}>
        <HeaderScheduling location={location} />

        {/* Cabeçalho com título e navegação */}
        <Stack 
          gap={1} 
          alignItems="center" 
          direction="row" 
          width="100%"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <PeopleAlt fontSize="small" />
            <Typography variant="body2" color="neutral700.main">
              Escolha um profissional:
            </Typography>
          </Stack>

          {/* Navegação - mostra apenas se houver mais de 3 profissionais */}
          {sortedPractitioners.length > PRACTITIONERS_PER_PAGE && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => setPage((prev) => prev - 1)}
                disabled={page === 0}
                sx={{ padding: "4px" }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: "40px", textAlign: "center" }}>
                {page + 1}/{totalPages}
              </Typography>
              
              <IconButton
                size="small"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={page >= totalPages - 1}
                sx={{ padding: "4px" }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>

        {/* Lista de profissionais com Box isolado para evitar colapso de margem */}
        <Box width="100%" mb={3}>
          <Stack gap={1}>
            {visiblePractitioners.map((el: any) => {
              const originalIndex = el.originalIndex;
              const isSelected = selectedPractitioner === originalIndex;

              return (
                <CardProfessional
                  key={originalIndex}
                  namePractitioner={el?.display}
                  healthcare={el?.healthcare?.display}
                  serviceCost={el?.serviceCost}
                  selectedPractitioner={isSelected}
                  onClick={() => setSelectedPractitioner(originalIndex)}
                />
              );
            })}
          </Stack>
        </Box>
        
        <Divider sx={{ width: "100%", background: "neutral400.main", mb: 2 }} />

        <Typography variant="body2" fontWeight={600} color="neutral700.main">
          Horários disponíveis
        </Typography>

        <SlotsMobile
          slots={slots}
          loading={loading}
          location={location}
          setRefetchSlots={setRefetchSlots}
          scheduleCompleted={scheduleCompleted}
        />
      </Stack>
    </ScreenMobile>
  );
}
